import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageCircle, Plug, RefreshCw, TriangleAlert, Unplug } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { InstagramOauthState } from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import {
  finishInstagramConnectAttempt,
  startInstagramConnectAttempt,
} from '@/Domain/components/Integration/Instagram/instagramConnectAnalytics';
import { IntegrationEnabledBadge } from '@/Domain/components/Integration/IntegrationEnabledBadge';
import { IntegrationStatusBadge } from '@/Domain/components/Integration/IntegrationStatusBadge';
import { IntegrationConnectionCard } from '@/Domain/components/Integration/components/IntegrationConnectionCard';
import { IntegrationIntroduction } from '@/Domain/components/Integration/components/IntegrationIntroduction';
import { IntegrationSection } from '@/Domain/components/Integration/components/IntegrationSection';
import { IntegrationToggleField } from '@/Domain/components/Integration/components/IntegrationToggleField';
import {
  AlertDialogFooter,
  AlertDialogHeader,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { InstagramIcon } from '@/Domain/components/icons/Instagram.icon';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import {
  trackIntegrationConnectFailed,
  trackIntegrationConnectStarted,
} from '@/Domain/hooks/Integration/integrationConnectAnalytics';
import { getCurrentScenarioId } from '@/context';

const INSTAGRAM_INTEGRATION_NAME = 'instagram' as const;
const INSTAGRAM_QUERY_KEY = [QueryKey.integration.get, INSTAGRAM_INTEGRATION_NAME] as const;
const INSTAGRAM_OAUTH_STATE_QUERY_KEY = [
  QueryKey.integration.instagramOauthState,
  'popup',
] as const;
// Its own state: only a `redirect`-mode one makes the callback return the browser to the
// app instead of rendering a page that talks to an opener this flow does not have.
const INSTAGRAM_REDIRECT_STATE_QUERY_KEY = [
  QueryKey.integration.instagramOauthState,
  'redirect',
] as const;
// Instagram authorizes whichever account the browser session already holds and offers
// no account picker, so a merchant signed into a personal account is told only
// "Something went wrong", by Instagram, before our callback runs. Re-authenticating is
// the documented way back to the chooser. Redirect mode, not popup: this state is
// fetched on the click, and a `window.open` after an await is treated as unsolicited.
const INSTAGRAM_REAUTH_STATE_QUERY_KEY = [
  QueryKey.integration.instagramOauthState,
  'redirect',
  true,
] as const;

const POPUP_FEATURES = 'width=600,height=800';
// Long enough that a quiet first hour is not read as a broken switch, short enough
// that a merchant who tested and saw nothing still gets told where to look.
const FIRST_INBOUND_WARN_AFTER_MS = 24 * 60 * 60 * 1_000;
const OAUTH_RECONCILE_INTERVAL_MS = 3_000;
const OAUTH_RECONCILE_TIMEOUT_MS = 120_000;

interface InstagramIntegrationParams {
  instagram_bot_active?: boolean;
  instagram_account_id?: string | null;
  instagram_username?: string | null;
  instagram_token_expires_at?: string | null;
  instagram_connected_at?: string | null;
  instagram_token_state?: string | null;
  instagram_first_inbound_at?: string | null;
  instagram_last_send_error_category?: string | null;
}

interface InstagramIntegrationResponse {
  name: 'instagram';
  params: InstagramIntegrationParams;
}

interface InstagramSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// A timestamp we cannot read must not silently suppress the warning forever, so an
// undatable connection is treated as old rather than fresh.
const isOlderThan = (iso: string | null, ageMs: number): boolean => {
  const parsed = iso ? Date.parse(iso) : Number.NaN;
  return Number.isNaN(parsed) ? true : Date.now() - parsed >= ageMs;
};

const formatDate = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '';

export const InstagramSheet = ({ open, onOpenChange }: InstagramSheetProps) => {
  const { t } = useTranslation(['integration']);
  const { capture } = usePostHogAnalytics();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [isLaunchingInTab, setIsLaunchingInTab] = useState(false);
  const [isSwitchingAccount, setIsSwitchingAccount] = useState(false);
  const popupPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const popupRef = useRef<Window | null>(null);

  const clearPopupPoll = useCallback(() => {
    if (popupPollRef.current !== null) {
      clearInterval(popupPollRef.current);
      popupPollRef.current = null;
    }
  }, []);

  // The sheet stays mounted when closed, so unmount cleanup alone leaves an abandoned
  // connect refetching every 3s for the rest of the reconcile window.
  useEffect(() => {
    if (!open) {
      clearPopupPoll();
      setPopupBlocked(false);
      setIsLaunchingInTab(false);
      setIsSwitchingAccount(false);
    }
    return () => {
      clearPopupPoll();
    };
  }, [clearPopupPoll, open]);

  const { data, isLoading, refetch } = useQuery<InstagramIntegrationResponse>({
    queryKey: INSTAGRAM_QUERY_KEY,
    enabled: open,
  });

  // The dialog URL is built server-side so the app id, scopes and redirect_uri have
  // one source of truth; a redirect_uri differing by a trailing slash fails the exchange.
  const {
    data: oauthState,
    isFetching: isOauthStateFetching,
    isError: isOauthStateError,
  } = useQuery<InstagramOauthState>({
    queryKey: INSTAGRAM_OAUTH_STATE_QUERY_KEY,
    enabled: open,
  });

  const accountId = data?.params?.instagram_account_id || null;
  const username = data?.params?.instagram_username || null;
  const connectedAt = data?.params?.instagram_connected_at ?? null;
  const tokenState = data?.params?.instagram_token_state ?? null;
  const firstInboundAt = data?.params?.instagram_first_inbound_at ?? null;
  const lastSendError = data?.params?.instagram_last_send_error_category ?? null;

  const isConnected = Boolean(accountId);
  const isEnabled = Boolean(data?.params?.instagram_bot_active);
  // Connected, subscribed, and yet no DM has ever arrived. Nearly always means the
  // message permission was never really granted, or was revoked, which no API reports.
  // A rejected send says the same thing outright, and says it for the account that
  // already received DMs before the permission was withdrawn — a confirmed failure,
  // so it skips the grace period below.
  const accessRefused = lastSendError === 'messages_access_off';
  // A merchant who connected a minute ago has not failed anything yet. The backend sweep
  // waits 48h before it pages; the sheet warns sooner because the person is right here.
  const awaitingFirstInbound =
    isConnected &&
    !firstInboundAt &&
    !accessRefused &&
    !isOlderThan(connectedAt, FIRST_INBOUND_WARN_AFTER_MS);
  const isUnverified = isConnected && !awaitingFirstInbound && (!firstInboundAt || accessRefused);
  // `expired` alone would be near-unreachable: the token is only marked expired once
  // it has actually lapsed, so a merchant who revokes access sees nothing for up to
  // 15 days while every send fails. The rejected send is the timely signal.
  // `refresh_failed` is deliberately absent — one failed nightly attempt on a token
  // still valid for two more weeks is an ops state, not a merchant one.
  const isTokenExpired = tokenState === 'expired';
  const needsReconnect = isTokenExpired || lastSendError === 'token_invalid';

  // The sheet renders on every Channels page load, so an unresolved key here would
  // take the whole page down rather than degrade one panel.
  const prerequisitesRaw = t('integration:instagram.prerequisites', { returnObjects: true });
  const prerequisites = Array.isArray(prerequisitesRaw) ? prerequisitesRaw : [];
  const canLaunchOAuth = !isOauthStateFetching && !isOauthStateError && Boolean(oauthState);

  const toggleMutation = useMutation<
    InstagramIntegrationResponse,
    Error,
    { name: typeof INSTAGRAM_INTEGRATION_NAME; params: { instagram_bot_active: boolean } }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: (response, variables) => {
      queryClient.setQueryData<InstagramIntegrationResponse>(INSTAGRAM_QUERY_KEY, (prev) => ({
        name: INSTAGRAM_INTEGRATION_NAME,
        params: { ...(prev?.params ?? {}), ...response.params },
      }));
      const name = t('integration:instagram.title');
      toast.success(
        variables.params.instagram_bot_active
          ? t('integrations:toast.enabled', { name })
          : t('integrations:toast.disabled', { name }),
      );
    },
    onError: () => {
      toast.error(t('integration:instagram.update_failed'));
    },
  });

  const handleToggleActive = useCallback(
    (checked: boolean) => {
      toggleMutation.mutate({
        name: INSTAGRAM_INTEGRATION_NAME,
        params: { instagram_bot_active: checked },
      });
    },
    [toggleMutation],
  );

  const disconnectMutation = useMutation({
    mutationKey: [QueryKey.integration.instagramDelete],
    onSuccess: () => {
      queryClient.setQueryData<InstagramIntegrationResponse>(INSTAGRAM_QUERY_KEY, {
        name: INSTAGRAM_INTEGRATION_NAME,
        params: {},
      });
      setConfirmOpen(false);
      capture(PostHogEventEnum.InstagramDisconnected);
    },
    onError: () => {
      toast.error(t('integration:instagram.disconnect_failed'));
    },
  });

  const handleConnect = useCallback(() => {
    // Every launch reuses the `instagram_oauth` window name, so a second click would
    // navigate an authorization already in progress away from itself. Reading `closed`
    // rather than tracking our own flag keeps a popup the user dismissed from blocking
    // the retry, and degrades to a fresh launch if COOP has severed the handle.
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.focus();
      return;
    }
    if (!oauthState) {
      toast.error(t('integration:instagram.update_failed'));
      return;
    }
    capture(PostHogEventEnum.InstagramOauthStarted, { mode: 'popup' });
    trackIntegrationConnectStarted(capture, 'instagram');
    const popup = window.open(oauthState.authorize_url, 'instagram_oauth', POPUP_FEATURES);
    if (!popup) {
      // Not a dead end: the callback honours a `redirect` state by returning the
      // browser to the Channels page, so the connect can finish in this tab. Mobile
      // Safari blocks popups by default, and Instagram is a phone-first channel.
      trackIntegrationConnectFailed(capture, 'instagram', 'popup_blocked');
      setPopupBlocked(true);
      return;
    }
    popupRef.current = popup;
    const attempt = startInstagramConnectAttempt(popup, getCurrentScenarioId());
    setPopupBlocked(false);

    clearPopupPoll();
    void queryClient.invalidateQueries({ queryKey: INSTAGRAM_OAUTH_STATE_QUERY_KEY });
    // Baseline on a fresh read: the cache can miss a connection made elsewhere, and a failed refetch returns the cache.
    const baseline = refetch().then(({ data: current, isError }) =>
      isError ? undefined : (current?.params?.instagram_connected_at ?? null),
    );
    const reconcileDeadline = Date.now() + OAUTH_RECONCILE_TIMEOUT_MS;
    const poll = setInterval(() => {
      if (Date.now() >= reconcileDeadline || getCurrentScenarioId() !== attempt.scenarioId) {
        clearPopupPoll();
        return;
      }
      void baseline.then(async (initialConnectedAt) => {
        const { data: refreshed } = await refetch();
        if (popupPollRef.current !== poll || getCurrentScenarioId() !== attempt.scenarioId) return;
        const refreshedAccountId = refreshed?.params?.instagram_account_id ?? null;
        const refreshedConnectedAt = refreshed?.params?.instagram_connected_at ?? null;
        const connectionChanged =
          initialConnectedAt !== undefined &&
          refreshedAccountId &&
          refreshedConnectedAt &&
          refreshedConnectedAt !== initialConnectedAt;
        if (!connectionChanged) return;
        clearPopupPoll();
        finishInstagramConnectAttempt(capture, attempt, 'success', 'connected', 'status_poll');
      });
    }, OAUTH_RECONCILE_INTERVAL_MS);
    popupPollRef.current = poll;
  }, [capture, clearPopupPoll, oauthState, queryClient, refetch, t]);

  const launchInTab = useCallback(
    async (forceReauth: boolean) => {
      if (forceReauth) setIsSwitchingAccount(true);
      else setIsLaunchingInTab(true);
      try {
        // Issued on the click and never held: a same-tab navigation is not gesture-bound, and a
        // state fetched earlier can have lapsed into the expired-link page this panel exists to
        // avoid. Removed straight after so nothing can replay the one we are about to spend.
        const key = forceReauth
          ? INSTAGRAM_REAUTH_STATE_QUERY_KEY
          : INSTAGRAM_REDIRECT_STATE_QUERY_KEY;
        const redirectState = await queryClient.fetchQuery<InstagramOauthState>({
          queryKey: key,
        });
        queryClient.removeQueries({ queryKey: key, exact: true });
        clearPopupPoll();
        capture(PostHogEventEnum.InstagramOauthStarted, {
          mode: 'redirect',
          force_reauth: forceReauth,
        });
        trackIntegrationConnectStarted(capture, 'instagram');
        // Left spinning: the tab is leaving, and re-enabling only invites a second launch.
        window.location.href = redirectState.authorize_url;
      } catch {
        trackIntegrationConnectFailed(capture, 'instagram', 'redirect_state_failed');
        toast.error(t('integration:instagram.continue_in_tab_failed'));
        if (forceReauth) setIsSwitchingAccount(false);
        else setIsLaunchingInTab(false);
      }
    },
    [capture, clearPopupPoll, queryClient, t],
  );

  const handleContinueInTab = useCallback(() => launchInTab(false), [launchInTab]);
  const handleUseDifferentAccount = useCallback(() => launchInTab(true), [launchInTab]);

  const handleDisconnect = useCallback(() => {
    disconnectMutation.mutate();
  }, [disconnectMutation]);

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (!disconnectMutation.isPending) onOpenChange(nextOpen);
      }}
    >
      <SheetContent
        className="max-w-xl w-full h-full pb-0"
        data-testid="instagram-sheet"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          if (!disconnectMutation.isPending) onOpenChange(false);
        }}
      >
        <SheetHeader className="px-0.5 items-center flex-row">
          <div className="flex items-center gap-3">
            <InstagramIcon className="size-8 shrink-0" />
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <SheetTitle data-testid="instagram-title">
                  {t('integration:instagram.title')}
                </SheetTitle>
                <IntegrationStatusBadge connected={isConnected} />
                <IntegrationEnabledBadge enabled={isEnabled} />
              </div>
            </div>
          </div>
          <IconButton
            onClick={() => onOpenChange(false)}
            disabled={disconnectMutation.isPending}
            variant="secondary"
            data-testid="instagram-close-button"
            className="shrink-0"
          >
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <div className="overflow-y-auto px-0.5 pb-6 space-y-6">
          {isLoading ? (
            <div
              className="flex items-center justify-center py-12 text-muted-foreground"
              data-testid="instagram-loading"
            >
              <Spinner className="size-5" />
            </div>
          ) : (
            <div className="space-y-6" data-testid="instagram-content">
              <IntegrationIntroduction
                description={t('integration:instagram.overview')}
                docsHref="https://docs.quickchat.ai/channels/instagram"
                prerequisites={prerequisites.map((_, index) => (
                  <Trans
                    key={index}
                    t={t}
                    i18nKey={`integration:instagram.prerequisites.${index}`}
                    components={[<strong key="lead" className="font-medium text-foreground" />]}
                  />
                ))}
              />

              {awaitingFirstInbound ? (
                <div
                  className="flex gap-3 rounded-lg border border-border bg-muted p-4"
                  data-testid="instagram-awaiting-first-inbound-panel"
                >
                  <MessageCircle className="size-5 shrink-0 text-muted-foreground" />
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-foreground">
                      {t('integration:instagram.awaiting_first_inbound.title')}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t('integration:instagram.awaiting_first_inbound.description', {
                        username: username ?? '',
                      })}
                    </p>
                  </div>
                </div>
              ) : null}

              {isUnverified ? (
                <div
                  className="flex gap-3 rounded-lg border border-warning bg-warning-muted p-4"
                  data-testid="instagram-unverified-panel"
                >
                  <TriangleAlert className="size-5 shrink-0 text-warning" />
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-foreground">
                      {t(
                        lastSendError === 'messages_access_off'
                          ? 'integration:instagram.unverified.title_access_off'
                          : 'integration:instagram.unverified.title',
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <Trans
                        t={t}
                        i18nKey="integration:instagram.unverified.description"
                        components={Array.from({ length: 4 }, (_, index) => (
                          <strong key={index} className="font-medium text-foreground" />
                        ))}
                      />
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t('integration:instagram.unverified.check', {
                        username: username ?? '',
                      })}
                    </p>
                  </div>
                </div>
              ) : null}

              {needsReconnect ? (
                <div
                  className="flex gap-3 rounded-lg border border-destructive bg-destructive-muted p-4"
                  data-testid="instagram-token-expired-panel"
                >
                  <TriangleAlert className="size-5 shrink-0 text-destructive" />
                  <p className="text-sm text-muted-foreground">
                    {t(
                      isTokenExpired
                        ? 'integration:instagram.token_expired'
                        : 'integration:instagram.token_rejected',
                    )}
                  </p>
                </div>
              ) : null}

              <IntegrationSection
                title={t('integrations:section.connect')}
                data-testid="instagram-connect-enable-section"
              >
                {isConnected ? (
                  <IntegrationConnectionCard
                    data-testid="instagram-connection-card"
                    // Account ID is a support detail and the token renews itself, so
                    // showing its expiry only reads as a deadline the merchant must act on.
                    details={[
                      {
                        label: t('integration:instagram.account'),
                        value: username ? `@${username}` : (accountId ?? ''),
                        'data-testid': 'instagram-connected-label',
                      },
                      {
                        label: t('integration:instagram.connected_since'),
                        value: formatDate(connectedAt),
                        'data-testid': 'instagram-connected-since',
                      },
                    ]}
                    actions={
                      <>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="flex-1"
                          onClick={handleConnect}
                          disabled={!canLaunchOAuth || disconnectMutation.isPending}
                          data-testid="instagram-reconnect-button"
                        >
                          <RefreshCw className="size-4" />
                          {t('integrations:reconnect')}
                        </Button>
                        <Button
                          type="button"
                          variant="danger"
                          size="sm"
                          className="flex-1"
                          onClick={() => setConfirmOpen(true)}
                          disabled={disconnectMutation.isPending}
                          data-testid="instagram-disconnect-button"
                        >
                          <Unplug className="size-4" />
                          {t('integrations:disconnect')}
                        </Button>
                      </>
                    }
                  />
                ) : (
                  <Button
                    type="button"
                    variant="dark"
                    size="sm"
                    className="w-full"
                    onClick={handleConnect}
                    // Launching without a server-issued state produces a dialog Meta rejects.
                    disabled={!canLaunchOAuth}
                    data-testid="instagram-connect-button"
                  >
                    <Plug className="size-4" />
                    {t('integrations:connect', { name: 'Instagram' })}
                  </Button>
                )}
                {!isConnected && (
                  <div className="flex flex-col gap-1.5">
                    <p className="text-xs text-muted-foreground">
                      {t('integration:instagram.active_account_note')}
                    </p>
                    <button
                      type="button"
                      className="cursor-pointer self-start text-xs font-medium text-foreground link-underline underline-offset-2 transition-colors hover:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={() => void handleUseDifferentAccount()}
                      disabled={isSwitchingAccount}
                      data-testid="instagram-use-different-account"
                    >
                      {t('integration:instagram.sign_in_as_different_account')}
                    </button>
                  </div>
                )}
                {popupBlocked && (
                  <div
                    className="flex flex-col gap-2 rounded-lg border border-border bg-muted p-3"
                    // Appears on a click that moves no focus: announced here, or missed entirely.
                    role="status"
                    aria-live="polite"
                    data-testid="instagram-popup-blocked"
                  >
                    <p className="text-sm text-foreground">
                      {t('integration:instagram.popup_blocked_title')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('integration:instagram.popup_blocked_description')}
                    </p>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="w-full"
                      onClick={() => void handleContinueInTab()}
                      loading={isLaunchingInTab}
                      data-testid="instagram-continue-in-tab"
                    >
                      {t('integration:instagram.continue_in_tab')}
                    </Button>
                  </div>
                )}
                <p className="text-xs text-foreground-subtle">
                  {t('integration:instagram.permissions_note')}
                </p>
              </IntegrationSection>

              <IntegrationSection
                title={t('integrations:section.settings')}
                data-testid="instagram-settings-section"
              >
                <IntegrationToggleField
                  id="instagram-enable-switch"
                  label={t('integrations:enable', { name: 'Instagram' })}
                  checked={isEnabled}
                  onCheckedChange={handleToggleActive}
                  disabled={!isConnected || toggleMutation.isPending}
                  data-testid="instagram-enable-switch"
                />
              </IntegrationSection>
            </div>
          )}
        </div>

        <AlertDialogPrimitive.Root
          open={confirmOpen}
          onOpenChange={(nextOpen) => {
            if (!disconnectMutation.isPending) setConfirmOpen(nextOpen);
          }}
        >
          <AlertDialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 backdrop-blur-sm z-20" />
          <AlertDialogPrimitive.Content className="absolute inset-0 flex items-center justify-center p-4 z-30 focus:outline-none">
            <div
              className="space-y-4 bg-card rounded-lg p-6 max-w-md w-full shadow-lg"
              data-testid="instagram-disconnect-dialog"
            >
              <AlertDialogHeader>
                <AlertDialogPrimitive.Title className="text-lg font-semibold text-foreground">
                  {t('integration:instagram.disconnect_confirm.title')}
                </AlertDialogPrimitive.Title>
              </AlertDialogHeader>
              <AlertDialogPrimitive.Description className="text-sm text-muted-foreground">
                {t('integration:instagram.disconnect_confirm.description')}
              </AlertDialogPrimitive.Description>
              <AlertDialogFooter>
                <AlertDialogPrimitive.Cancel asChild>
                  <Button
                    size="md"
                    variant="secondary"
                    disabled={disconnectMutation.isPending}
                    data-testid="instagram-disconnect-cancel-button"
                  >
                    {t('integration:instagram.disconnect_confirm.cancel')}
                  </Button>
                </AlertDialogPrimitive.Cancel>
                <Button
                  variant="danger"
                  size="md"
                  onClick={handleDisconnect}
                  disabled={disconnectMutation.isPending}
                  data-testid="instagram-disconnect-confirm-button"
                >
                  {disconnectMutation.isPending ? <Spinner className="size-4" /> : null}
                  {t('integration:instagram.disconnect_confirm.confirm')}
                </Button>
              </AlertDialogFooter>
            </div>
          </AlertDialogPrimitive.Content>
        </AlertDialogPrimitive.Root>
      </SheetContent>
    </Sheet>
  );
};
